import { memo, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useGetServiceExecHistoryQuery } from '../../../../redux/api';
import {
  EXEC_HISTORY_ROW_COUNT,
  formatExecHistoryDate,
  formatExecHistoryStatus,
  mapServiceExecHistoryResponse,
  submitTransactionSearch,
} from '../utils/apiServiceUtils';
import { K, apiLabel, apiT } from '../apiServiceI18n';

const GRID_COLS = '50px minmax(180px, 220px) 1fr minmax(100px, 140px)';

function ApiServiceExecutionHistoryDrawer({ target, onClose }) {
  const { t } = useTranslation();
  const { baseUrl } = useSelector((state) => state.queryParams);

  const account = target?.account ?? '';
  const wfgName = target?.workflowGroup ?? '';
  const wfName = target?.workflowName ?? '';

  const historyQuery = useGetServiceExecHistoryQuery(
    {
      account,
      wfg_name: wfgName,
      wf_name: wfName,
      rowcnt: EXEC_HISTORY_ROW_COUNT,
    },
    { skip: !target || !account || !wfgName || !wfName }
  );

  const { rows, error: mapError } = useMemo(
    () => mapServiceExecHistoryResponse(historyQuery.data),
    [historyQuery.data]
  );

  const isLoading = historyQuery.isFetching;
  const canOpenTxnSearch = Boolean(account && wfgName && wfName);

  const openTransactionSearch = () => {
    if (!canOpenTxnSearch) return;
    submitTransactionSearch(baseUrl, {
      account,
      workflowGroup: wfgName,
      workflowName: wfName,
    });
  };

  const emDash = t(K.API_SVC_EM_DASH);

  return (
    <Drawer anchor="left" open={!!target} onClose={onClose}>
      <Box
        sx={{
          width: 920,
          maxWidth: '95vw',
          minWidth: 640,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f6f7fa',
        }}
      >
        <Box
          sx={{
            bgcolor: '#eceff3',
            borderBottom: '1px solid #d5d9e0',
            px: 1.25,
            py: 0.9,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.1rem',
              color: '#5f6878',
              textTransform: 'uppercase',
              letterSpacing: 0.2,
            }}
          >
            {t(K.API_SVC_EXEC_HISTORY_TITLE)}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t(K.API_SVC_CLOSE_EXEC_HISTORY)}
            sx={{ color: '#6a7382', border: '1px solid #d2d8e2', borderRadius: 1 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: '1px solid #d5d9e0',
            bgcolor: '#f8f9fc',
            color: '#4b5566',
          }}
        >
          <Typography variant="body2">
            <b>{apiLabel(K.ACCOUNT)}:</b> {account || emDash}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.LBL_NAME)}:</b> {target?.name || emDash}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.WORKFLOW_GROUP)}:</b> {wfgName || emDash}
          </Typography>
          <Typography variant="body2">
            <b>{apiLabel(K.WORKFLOW)}:</b> {wfName || emDash}
          </Typography>
        </Box>

        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: '1px solid #d5d9e0',
            bgcolor: '#fff',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Typography variant="body2" color="text.secondary">
              {apiT(K.API_SVC_EXEC_HISTORY_SHOWING, { 0: EXEC_HISTORY_ROW_COUNT })}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ManageSearchIcon fontSize="small" />}
              endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
              onClick={openTransactionSearch}
              disabled={!canOpenTxnSearch}
              sx={{
                flexShrink: 0,
                textTransform: 'none',
                fontWeight: 600,
                color: '#5f6878',
                borderColor: '#d2d8e2',
                whiteSpace: 'nowrap',
                '&:hover': { bgcolor: '#eef1f6', borderColor: '#b8c3d4' },
              }}
            >
              {t(K.API_SVC_TXN_SEARCH)}
            </Button>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, px: 1, py: 1, overflow: 'auto' }}>
          {(mapError || historyQuery.error) && !isLoading && (
            <Alert severity="error" sx={{ m: 1 }}>
              {mapError ||
                historyQuery.error?.data?.message ||
                historyQuery.error?.error ||
                t(K.API_SVC_EXEC_HISTORY_FAILED)}
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ minWidth: 600 }}>
              <Box
                role="row"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLS,
                  bgcolor: '#eceff3',
                  color: '#5f6878',
                  fontWeight: 700,
                  border: '1px solid #d5d9e0',
                  textTransform: 'uppercase',
                  fontSize: '0.78rem',
                }}
              >
                <Box sx={{ px: 1, py: 0.75 }}>#</Box>
                <Box sx={{ px: 1, py: 0.75 }}>{t(K.CREATED_DATE)}</Box>
                <Box sx={{ px: 1, py: 0.75 }}>{t(K.STATUS)}</Box>
                <Box sx={{ px: 1, py: 0.75, textAlign: 'right' }}>{t(K.API_SVC_TXN_SEARCH)}</Box>
              </Box>

              {rows.map((row, idx) => (
                <Box
                  key={`${row.timestamp}-${row.transactionIds.join('-')}-${idx}`}
                  role="row"
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: GRID_COLS,
                    borderLeft: '1px solid #e1e5ec',
                    borderRight: '1px solid #e1e5ec',
                    borderBottom: '1px solid #e1e5ec',
                    bgcolor: idx % 2 === 0 ? '#fff' : '#f9fafc',
                    '&:hover': { bgcolor: '#f0f3f8' },
                  }}
                >
                  <Box sx={{ px: 1, py: 0.75, fontSize: '0.82rem' }}>{idx + 1}</Box>
                  <Box sx={{ px: 1, py: 0.75, whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                    {formatExecHistoryDate(row.timestamp)}
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.75,
                      wordBreak: 'break-word',
                      lineHeight: 1.35,
                      fontSize: '0.82rem',
                    }}
                  >
                    {formatExecHistoryStatus(row.status)}
                  </Box>
                  <Box
                    sx={{
                      px: 1,
                      py: 0.75,
                      textAlign: 'right',
                      fontSize: '0.82rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.transactionIds.length ? row.transactionIds.join(', ') : emDash}
                  </Box>
                </Box>
              ))}

              {!rows.length && !mapError && !historyQuery.error && (
                <Box
                  sx={{
                    p: 2,
                    border: '1px solid #d0d0d0',
                    borderTop: 0,
                    bgcolor: '#fff',
                    color: 'text.secondary',
                  }}
                >
                  {t(K.API_SVC_EXEC_HISTORY_EMPTY)}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

export default memo(ApiServiceExecutionHistoryDrawer);
